import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import BookForm from "../components/BookForm";
import { createBook, updateBook } from "../services/booksService";
import { generateCoverIdeas } from "../services/geminiService";
import { auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import Stepper from "../components/Stepper";

const CreateBook = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [ideas, setIdeas] = useState([]);
    const [selectedConcept, setSelectedConcept] = useState(null);
    const [bookId, setBookId] = useState(null);
    const [bookData, setBookData] = useState(null);
    const [user, setUser] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUser(user);
            } else {
                navigate("/");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const handleCreateBook = async (data) => {
        try {
            setLoading(true);
            const currentUser = auth.currentUser;

            if (!currentUser) {
                alert("Please login first.");
                return;
            }

            const newBookId = await createBook({
                ...data,
                userId: currentUser.uid,
            });

            setBookId(newBookId);
            setBookData(data);

            const coverIdeas = await generateCoverIdeas(data);

            // coverIdeas is expected to be an array of objects
            setIdeas(Array.isArray(coverIdeas) ? coverIdeas : []);

            // Automatically select the first option as default
            if (Array.isArray(coverIdeas) && coverIdeas.length > 0) {
                setSelectedConcept(coverIdeas[0]);
            }

            console.log("Book Created:", newBookId);
        } catch (error) {
            console.error("Create Book Error:", error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleNextStep = async () => {
        if (!bookId || !selectedConcept) return;

        try {
            setLoading(true);
            // Save the selected suggestion to the book document in Firestore
            await updateBook(bookId, {
                selectedSuggestion: selectedConcept,
            });
            // Navigate to step 2 (Cover Selection)
            navigate(`/covers?bookId=${bookId}`);
        } catch (error) {
            console.error("Error saving concept selection:", error);
            alert("Failed to save cover selection. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F8F7F4] to-[#F0EDE6] font-serif">
            <nav className="bg-white px-6 md:px-10 flex items-center justify-between h-[72px] shadow-[0_2px_12px_rgba(0,0,0,0.08)] border-b border-[#F0F0F0]">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-[#DC2626] rounded-lg flex items-center justify-center text-lg">📚</div>
                    <span className="text-[#1E3A8A] font-extrabold text-xl tracking-tight">
                        Pub<span className="text-[#DC2626]">Tech</span>
                    </span>
                </div>

                <div className="hidden md:flex gap-8 items-center">
                    <Link
                        to="/dashboard"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Dashboard
                    </Link>
                    <Link
                        to="/create-book"
                        className="text-[#1E3A8A] border-b-2 border-[#DC2626] text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Create Book
                    </Link>
                    <Link
                        to="/covers"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Covers
                    </Link>
                    <Link
                        to="/upload"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Upload
                    </Link>
                    <Link
                        to="/preview"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Preview
                    </Link>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-xs font-semibold hidden sm:inline">{user?.email}</span>
                    <button
                        onClick={handleLogout}
                        className="px-4 py-2 bg-transparent hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#DC2626] rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150"
                    >
                        Log Out
                    </button>
                </div>
            </nav>

            <div className="py-10 px-4">
                <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-8 text-center">
                    <div className="inline-flex items-center gap-2 bg-blue-900/10 border border-blue-900/20 rounded-full px-4 py-1.5 mb-4">
                        <span className="w-1.5 h-1.5 bg-red-600 rounded-full inline-block" />
                        <span className="text-blue-900 text-xs font-bold tracking-widest uppercase">
                            Step 1 of 3
                        </span>
                    </div>
                    <h1 className="text-4xl font-extrabold text-blue-900 mb-3 tracking-tight">
                        Create Your <span className="text-red-600">Book</span>
                    </h1>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        Tell us about your book and we'll generate AI-powered cover concepts tailored to your story.
                    </p>
                </div>

                <Stepper currentStep={1} />

                {!bookId ? (
                    <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="h-1.5 bg-gradient-to-r from-red-600 to-blue-900" />
                        <div className="p-8">
                            <BookForm onSubmit={handleCreateBook} />
                        </div>
                    </div>
                ) : (
                    <div className="mt-8 bg-gradient-to-r from-blue-50 to-white rounded-2xl shadow-lg border border-blue-100 p-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-blue-900">Book Details Saved!</h2>
                                <p className="text-sm text-gray-600">
                                    <span className="font-semibold">{bookData?.title}</span> by <span className="font-semibold">{bookData?.author}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="mt-8 flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-xl border border-gray-100">
                        <div className="relative">
                            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-100 border-t-red-600"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-2xl">✨</span>
                            </div>
                        </div>
                        <p className="mt-6 text-gray-700 font-medium text-lg animate-pulse">
                            Generating Cover Concepts with Gemini...
                        </p>
                        <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
                    </div>
                )}

                {!loading && ideas.length > 0 && (
                    <div className="mt-10">
                        <div className="mb-8">
                            <div className="flex items-center gap-3 mb-2">
                                <span className="text-3xl">🎨</span>
                                <h3 className="font-extrabold text-3xl text-blue-900">
                                    Select a Cover Concept
                                </h3>
                            </div>
                            <p className="text-gray-600 text-lg">
                                Choose one of the AI-generated concepts. We'll create custom front and back covers based on your selection.
                            </p>
                        </div>

                        <div className="grid gap-6 md:grid-cols-2">
                            {ideas.map((concept, index) => {
                                const isSelected = selectedConcept && selectedConcept.theme === concept.theme;
                                return (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedConcept(concept)}
                                        className={`cursor-pointer p-6 rounded-2xl border-2 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl flex flex-col justify-between ${isSelected
                                                ? "border-red-600 bg-gradient-to-br from-red-50 to-blue-50 shadow-lg ring-2 ring-red-600/20"
                                                : "border-gray-200 bg-white hover:border-blue-300"
                                            }`}
                                    >
                                        <div>
                                            <div className="flex justify-between items-center mb-4">
                                                <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${isSelected
                                                        ? "bg-red-600 text-white"
                                                        : "bg-blue-100 text-blue-700"
                                                    }`}>
                                                    Concept {index + 1}
                                                </span>
                                                {isSelected && (
                                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold">
                                                        ✓
                                                    </span>
                                                )}
                                            </div>
                                            <h4 className="font-bold text-xl text-blue-900 mb-2">
                                                {concept.theme}
                                            </h4>
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Colors:</span>
                                                <span className="text-sm font-semibold text-gray-800">{concept.colors}</span>
                                            </div>
                                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                                <p className="text-sm text-gray-700 italic leading-relaxed">
                                                    "{concept.imageIdea}"
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-10 flex justify-between items-center">
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="text-gray-600 hover:text-blue-900 font-semibold transition-colors duration-200 flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Dashboard
                            </button>
                            <button
                                onClick={handleNextStep}
                                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-10 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 flex items-center gap-2"
                            >
                                Continue to Cover Selection
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
    );
};

/* ── Shared style tokens (Commented out - migrated to Tailwind CSS) ────────
const navStyles = {
    page: {
        minHeight: "100vh",
        background: "linear-gradient(160deg, #F8F7F4 0%, #F0EDE6 100%)",
        fontFamily: "'Georgia', 'Times New Roman', serif",
    },
    nav: {
        background: "#FFFFFF",
        padding: "0 40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "72px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        borderBottom: "1px solid #F0F0F0",
    },
    navLeft: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    logo: {
        width: "36px",
        height: "36px",
        background: "#DC2626",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
    },
    logoText: {
        color: "#1E3A8A",
        fontWeight: "800",
        fontSize: "20px",
        letterSpacing: "-0.02em",
    },
    logoRed: {
        color: "#DC2626",
    },
    navLinks: {
        display: "flex",
        gap: "32px",
        alignItems: "center",
    },
    navLink: {
        color: "#4B5563",
        fontSize: "14px",
        fontWeight: "500",
        textDecoration: "none",
        transition: "color 0.15s",
        padding: "8px 0",
        borderBottom: "2px solid transparent",
    },
    navLinkActive: {
        color: "#1E3A8A",
        borderBottom: "2px solid #DC2626",
    },
    navRight: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
    },
    userEmail: {
        color: "#6B7280",
        fontSize: "13px",
        fontWeight: "500",
    },
    logoutBtn: {
        padding: "8px 16px",
        background: "transparent",
        color: "#DC2626",
        border: "1px solid #DC2626",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.15s",
    },
};
*/

export default CreateBook;